import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Mail, MapPin, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed. Briefings will arrive from Mission Control.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative border-t border-white/8 bg-[#0A0A0C]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex items-center justify-center w-9 h-9 rounded-md bg-gradient-to-br from-[#FF8C00] to-[#FF5A00]">
              <Zap className="w-5 h-5 text-[#0A0A0C]" strokeWidth={3} />
            </span>
            <span className="font-display font-black text-xl">
              perfo<span className="text-[#FF8C00]">range</span>
            </span>
          </Link>
          <p className="mt-5 text-[#A1A1AA] text-sm leading-relaxed max-w-sm">
            The operating system for school esports programs — a structured pathway, a defensible wellness framework, and a credible track from classroom to career.
          </p>
          <form onSubmit={onSubscribe} className="mt-6 flex gap-2 max-w-md" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@school.edu.in"
              data-testid="newsletter-email-input"
              className="flex-1 px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-sm"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit"
              className="px-4 py-3 rounded-lg bg-[#FF8C00] text-[#0A0A0C] text-sm font-bold hover:bg-[#FFA333] disabled:opacity-60 transition-colors"
            >
              {loading ? "..." : "Subscribe"}
            </button>
          </form>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display font-bold text-sm uppercase tracking-widest text-white mb-4">Verticals</h4>
          <ul className="space-y-3 text-sm text-[#A1A1AA]">
            <li><Link to="/academy" className="hover:text-white">Academy &amp; Performance Lab</Link></li>
            <li><Link to="/content-agency" className="hover:text-white">Career Ecosystem</Link></li>
            <li><Link to="/perforange-os" className="hover:text-white">Perforange OS</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display font-bold text-sm uppercase tracking-widest text-white mb-4">Company</h4>
          <ul className="space-y-3 text-sm text-[#A1A1AA]">
            <li><Link to="/about" className="hover:text-white">About</Link></li>
            <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
            <li><Link to="/principals-corner" className="hover:text-white">Principal's Corner</Link></li>
            <li><Link to="/partnership" className="hover:text-white">Partnership</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display font-bold text-sm uppercase tracking-widest text-white mb-4">Mission Control</h4>
          <ul className="space-y-3 text-sm text-[#A1A1AA]">
            <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-[#FF8C00] shrink-0" /> <span className="break-all">kmukherjee@perforange.com</span></li>
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#FF8C00] shrink-0" /> Mumbai, India</li>
            <li>
              <Link to="/contact" className="inline-flex items-center gap-1 text-[#FF8C00] hover:text-[#FFA333]" data-testid="footer-contact-link">
                Start a conversation <ArrowUpRight className="w-4 h-4" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/8 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-[#6a6a70]">
          <p>© {new Date().getFullYear()} Perforange Performance Systems Pvt. Ltd. All rights reserved.</p>
          <p className="font-mono-tech">Built for India&apos;s 2026 AVGC mandate · Curriculum compliance mapping &amp; ISO 45001 alignment</p>
        </div>
      </div>
    </footer>
  );
}
